// components/patient/Landing.jsx
import React from 'react';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/Button';
import { useBrand } from '../../contexts/BrandContext';

export function Landing({ onNavigate }) {
  const { brand } = useBrand();
  
  return (
    <div style={{ fontFamily: 'Libre Franklin, sans-serif' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Libre+Franklin:wght@400;600;700&family=Merriweather:wght@700;900&display=swap');
        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
      
      {/* Hero Section */}
      <section style={{
        background: brand.heroImage
          ? `linear-gradient(rgba(26,54,93,0.65), rgba(26,54,93,0.65)), url(${brand.heroImage}) center/cover no-repeat`
          : brand.c.heroBg,
        color: 'white',
        padding: '0 24px 80px',
        textAlign: 'center'
      }}>
        {brand.headerLogo && (
          <div style={{
            maxWidth: 1800,
            margin: '0 auto',
            padding: '20px 0',
            display: 'flex',
            alignItems: 'center'
          }}>
            <img
              src={brand.headerLogo}
              alt={brand.name}
              style={{ height: 48, objectFit: 'contain' }}
            />
          </div>
        )}
        <div style={{
          maxWidth: 900,
          margin: '0 auto',
          animation: 'fadeUp 0.6s ease-out'
        }}>
          <h1 style={{
            fontSize: 52,
            fontWeight: 900,
            marginBottom: 24,
            fontFamily: "'Merriweather', serif",
            lineHeight: 1.2
          }}>
            {brand.hero.headline}
          </h1>
          <p style={{
            fontSize: 20,
            marginBottom: 40,
            opacity: 0.95,
            lineHeight: 1.6,
            maxWidth: 700,
            margin: '0 auto 40px'
          }}>
            {brand.hero.subhead}
          </p>
          <Button
            size="lg"
            variant="gold"
            onClick={() => onNavigate(brand.isB2B ? 'register' : 'pilot')}
          >
            {brand.hero.cta}
          </Button>
          
          <div style={{ marginTop: 16 }}>
            <span style={{ fontSize: 15, opacity: 0.9 }}>
              Already have an account?{' '}
              <span
                onClick={() => onNavigate('login')}
                style={{
                  fontWeight: 700,
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                Log in
              </span>
            </span>
          </div>
          
          {/* Stats */}
          <div style={{
            display: 'flex',
            gap: 48,
            marginTop: 64,
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            {(brand.isB2B ? [
              { value: '$400B+', label: 'Annual Hospital Spend' },
              { value: '15-30%', label: 'Lost to Middlemen' },
              { value: '2-4 Weeks', label: 'Deployment Time' }
            ] : [
              { value: '~80%', label: 'Average Savings' },
              { value: '$47', label: 'Avg Cost Per Rx' },
              { value: '2.3 Days', label: 'Avg Delivery' }
            ]).map((stat, i) => (
              <div key={i}>
                <div style={{
                  fontSize: 48,
                  fontWeight: 900,
                  fontFamily: "'Merriweather', serif",
                  marginBottom: 8
                }}>
                  {stat.value}
                </div>
                <div style={{
                  fontSize: 14,
                  opacity: 0.9,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: 600
                }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Trust Bar */}
      <section style={{
        background: brand.c.trustBar,
        color: 'white',
        padding: '20px 24px'
      }}>
        <div style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          gap: 32,
          justifyContent: 'center',
          flexWrap: 'wrap',
          fontSize: 13,
          fontWeight: 600
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="shield" size={16} />
            HIPAA Compliant
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="lock" size={16} />
            DEA Licensed
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="package" size={16} />
            DSCSA Tracked
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="truck" size={16} />
            UPS Healthcare 3PL
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="globe" size={16} />
            50 States Licensed
          </div>
        </div>
      </section>
      
      {/* How It Works */}
      <section style={{
        background: 'white',
        padding: '80px 24px'
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 40,
            fontWeight: 900,
            textAlign: 'center',
            marginBottom: 60,
            fontFamily: "'Merriweather', serif",
            color: brand.c.navy
          }}>
            How {brand.name} Works
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 48
          }}>
            {(brand.isB2B ? [
              {
                step: '1',
                title: 'Connect Your Vendors',
                description: 'Link your existing GPO and distributor accounts to import pricing data automatically.'
              },
              {
                step: '2',
                title: 'Compare & Save',
                description: 'See side-by-side pricing: Your vendor vs GPO vs Direct wholesale cost.'
              },
              {
                step: '3',
                title: 'Order Direct',
                description: 'Buy direct when we save you money, or use your vendor when they win on price.'
              }
            ] : [
              {
                step: '1',
                title: 'Upload Prescription',
                description: 'Upload your prescription or have your doctor send it electronically to our pharmacy.'
              },
              {
                step: '2',
                title: 'Pharmacist Review',
                description: 'Licensed pharmacist verifies and approves your prescription for fulfillment.'
              },
              {
                step: '3',
                title: 'Fast Delivery',
                description: 'Medication ships directly from manufacturer to your door in 2-3 business days.'
              }
            ]).map((item, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  width: 80,
                  height: 80,
                  background: brand.c.accent,
                  borderRadius: '50%',
                  margin: '0 auto 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 36,
                  fontWeight: 700,
                  fontFamily: "'Merriweather', serif"
                }}>
                  {item.step}
                </div>
                <h3 style={{
                  fontSize: 22,
                  fontWeight: 700,
                  marginBottom: 12,
                  color: brand.c.navy
                }}>
                  {item.title}
                </h3>
                <p style={{
                  color: '#6B7280',
                  lineHeight: 1.6,
                  fontSize: 15
                }}>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Supply Chain Comparison (if applicable) */}
      {brand.showSupplyChain && (
        <section style={{
          background: '#F9FAFB',
          padding: '80px 24px'
        }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <h2 style={{
              fontSize: 40,
              fontWeight: 900,
              textAlign: 'center',
              marginBottom: 16,
              fontFamily: "'Merriweather', serif",
              color: brand.c.navy
            }}>
              {brand.isB2B ? 'Traditional Supply Chain vs Direct' : 'Traditional vs Direct Pricing'}
            </h2>
            <p style={{
              textAlign: 'center',
              fontSize: 18,
              color: '#6B7280',
              marginBottom: 48,
              maxWidth: 700,
              margin: '0 auto 48px'
            }}>
              {brand.isB2B 
                ? 'See where your dollars go in traditional procurement vs direct distribution'
                : 'Compare what you pay through traditional channels vs direct from manufacturer'
              }
            </p>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 24
            }}>
              {/* Traditional */}
              <div style={{
                background: 'white',
                borderRadius: 12,
                padding: 32,
                border: '2px solid #FEE2E2'
              }}>
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginBottom: 20,
                  color: '#EF4444'
                }}>
                  ❌ Traditional
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{
                    padding: '12px 16px',
                    background: brand.c.stripeOdd,
                    borderRadius: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                      Manufacturer
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>$10.00</div>
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: brand.c.stripeEven,
                    borderRadius: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                      {brand.isB2B ? 'GPO Fee (7%)' : 'PBM Fee'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>+$5.60</div>
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: brand.c.stripeOdd,
                    borderRadius: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                      {brand.isB2B ? 'Distributor Markup' : 'Pharmacy Markup'}
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>+$22.40</div>
                  </div>
                  <div style={{
                    padding: '16px',
                    background: '#FEE2E2',
                    borderRadius: 8,
                    marginTop: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#991B1B', marginBottom: 4, fontWeight: 600 }}>
                      Your Cost
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#EF4444' }}>
                      $38.00
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Direct */}
              <div style={{
                background: 'white',
                borderRadius: 12,
                padding: 32,
                border: `2px solid ${brand.c.accent}`
              }}>
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginBottom: 20,
                  color: brand.c.accent
                }}>
                  ✓ {brand.name} Direct
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{
                    padding: '12px 16px',
                    background: brand.c.stripeOdd,
                    borderRadius: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                      Manufacturer
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>$10.00</div>
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: brand.c.stripeEven,
                    borderRadius: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                      Fulfillment + Shipping
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>+$2.00</div>
                  </div>
                  <div style={{
                    padding: '12px 16px',
                    background: brand.c.stripeOdd,
                    borderRadius: 8,
                    opacity: 0.3
                  }}>
                    <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                      No middlemen
                    </div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>+$0.00</div>
                  </div>
                  <div style={{
                    padding: '16px',
                    background: '#D1FAE5',
                    borderRadius: 8,
                    marginTop: 8
                  }}>
                    <div style={{ fontSize: 13, color: '#065F46', marginBottom: 4, fontWeight: 600 }}>
                      Your Cost
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 900, color: '#10B981' }}>
                      $12.00
                    </div>
                    <div style={{
                      marginTop: 8,
                      padding: '6px 12px',
                      background: '#065F46',
                      color: 'white',
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: 700,
                      textAlign: 'center'
                    }}>
                      Save 68% ($26.00)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      
      {/* CTA Section */}
      <section style={{
        background: brand.c.heroBg,
        color: 'white',
        padding: '80px 24px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 40,
            fontWeight: 900,
            marginBottom: 20,
            fontFamily: "'Merriweather', serif"
          }}>
            {brand.isB2B ? 'Ready to Cut Procurement Costs?' : 'Ready to Save on Prescriptions?'}
          </h2>
          <p style={{
            fontSize: 18,
            marginBottom: 32,
            opacity: 0.95
          }}>
            {brand.isB2B 
              ? 'Connect your vendors and see how much you can save with direct distribution'
              : 'Join thousands of patients saving 70-80% on their medications'
            }
          </p>
          <Button
            size="lg"
            variant="gold"
            onClick={() => onNavigate(brand.isB2B ? 'register' : 'pilot')}
          >
            Get Started Now
          </Button>
        </div>
      </section>
    </div>
  );
}
